/**
 * Row actions: what a run, task, decision or landing row offers in a list without opening the
 * inspector. Everything comes from the registry (`actions(entity, ctx)`), so a key, label or
 * `viko` command shown on a row is the one the inspector footer, the status line and ⌘K show.
 * A row lists its top enabled verbs; `⏎` (the row itself), a decision's numbered answers and the
 * verbs its answer bar already labels are not repeated.
 */

import { type Action, type ActionCtx, type ActionEntity, actionForKey, actions } from './actions.js'
import type { InboxRow, NowRow } from './now-inbox.js'

export const ROW_ACTIONS_MAX = 3

export interface RowActionOptions {
  /** Keys the list already owns (triage keeps `s` `r` `d` `e`): those verbs are not offered. */
  skipKeys?: ReadonlySet<string>
  max?: number
}

/** Enabled, non-repeating verbs of `entity`, in registry order. */
export function rowActionList(
  entity: ActionEntity | undefined,
  ctx: ActionCtx,
  o: RowActionOptions = {},
): Action[] {
  if (!entity) return []
  return actions(entity, ctx).filter(
    (a) => !a.option && !a.inBar && a.key !== '⏎' && !a.disabledReason && !o.skipKeys?.has(a.key),
  )
}

/** The verbs a row shows as buttons with keycaps. */
export const topRowActions = (list: readonly Action[], max = ROW_ACTIONS_MAX): Action[] =>
  list.slice(0, max)

/** The verb this key runs on the row, if any (all enabled verbs, not just the visible top). */
export const rowActionForKey = (list: readonly Action[], e: KeyboardEvent): Action | undefined =>
  actionForKey(list, e)

const landMachine = (i: NonNullable<InboxRow['item']>) =>
  i.machineId ?? (i.body as { machineId?: string } | null)?.machineId

/** The entity a Needs-you / Inbox row stands for (a decision has its own answer keys). */
export function inboxRowEntity(row: InboxRow): ActionEntity | undefined {
  const item = row.item
  if (row.kind === 'decision' || !item) return undefined
  if (item.type === 'land' && item.land) {
    const machineId = landMachine(item)
    return {
      kind: 'landing',
      id: item.land.workstreamId ?? item.id,
      title: row.title,
      ...(item.land.branch ? { branch: item.land.branch } : {}),
      ...(machineId ? { machineId } : {}),
      workstreamId: item.land.workstreamId,
      ...(item.runId ? { runId: item.runId } : {}),
      ...(row.projectId ? { projectId: row.projectId } : {}),
    }
  }
  return undefined
}

/** A Home Running row is a run. */
export const nowRowEntity = (row: NowRow): ActionEntity => ({
  kind: 'run',
  id: row.runId,
  title: row.title,
  live: row.live,
})
